package com.sweetlime.dungeonamigo.presentation.features.session

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.dungeonamigo.cache.Monsters
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.model.CharacterCalculator
import com.sweetlime.dungeonamigo.model.DiceRoll
import com.sweetlime.dungeonamigo.model.DmEntity
import com.sweetlime.dungeonamigo.model.DmEntitySource
import com.sweetlime.dungeonamigo.model.RollEvent
import com.sweetlime.dungeonamigo.model.SessionParticipant
import com.sweetlime.dungeonamigo.model.SessionRoom
import com.sweetlime.dungeonamigo.repository.AuthRepository
import com.sweetlime.dungeonamigo.repository.CharacterRepository
import com.sweetlime.dungeonamigo.repository.SessionRepository
import com.sweetlime.dungeonamigo.repository.UserRepository
import com.sweetlime.dungeonamigo.util.currentTimeMillis
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlin.random.Random

sealed class InitiativeEntry {
    data class PlayerEntry(val participant: SessionParticipant) : InitiativeEntry()
    data class EntityEntry(val entity: DmEntity) : InitiativeEntry()
}

sealed class SessionState {
    object Idle : SessionState()
    object Loading : SessionState()
    data class InSession(
        val room: SessionRoom,
        val participants: List<SessionParticipant>,
        val rollFeed: List<RollEvent>,
        val myParticipant: SessionParticipant?,
        val isDm: Boolean,
        val initiativeOrder: List<InitiativeEntry>,
        val dmEntities: List<DmEntity> = emptyList(),
    ) : SessionState()
    data class Error(val message: String) : SessionState()
}

class SessionViewModel(
    private val sessionRepo: SessionRepository,
    private val characterRepo: CharacterRepository,
    private val authRepo: AuthRepository,
    private val userRepo: UserRepository,
) : ViewModel() {

    private val _sessionState = MutableStateFlow<SessionState>(SessionState.Idle)
    val sessionState: StateFlow<SessionState> = _sessionState.asStateFlow()

    // One-shot error events for DM entity operations (Firestore write failures, rules errors, etc.)
    private val _entityError = MutableSharedFlow<String>(extraBufferCapacity = 1)
    val entityError: SharedFlow<String> = _entityError.asSharedFlow()

    private val _roomCodeInput = MutableStateFlow("")
    val roomCodeInput: StateFlow<String> = _roomCodeInput.asStateFlow()

    private val _availableCharacters = MutableStateFlow<List<Character>>(emptyList())
    val availableCharacters: StateFlow<List<Character>> = _availableCharacters.asStateFlow()

    private val _currentTurnIndex = MutableStateFlow(0)
    val currentTurnIndex: StateFlow<Int> = _currentTurnIndex.asStateFlow()

    private var activeRoomId: String? = null
    private var activeUserId: String? = null
    private var sessionJob: Job? = null

    init {
        val userId = authRepo.currentUserId()
        if (userId != null) {
            viewModelScope.launch {
                characterRepo.getCharacters(userId)
                    .catch { /* ignore list errors */ }
                    .collect { chars -> _availableCharacters.value = chars }
            }
        }
    }

    fun onRoomCodeInput(value: String) {
        _roomCodeInput.value = value.uppercase().take(4)
    }

    fun createRoom(campaignName: String) {
        val userId = authRepo.currentUserId() ?: run {
            _sessionState.value = SessionState.Error("Not signed in")
            return
        }
        viewModelScope.launch {
            _sessionState.value = SessionState.Loading
            runCatching {
                val profile = userRepo.getUserProfile(userId)
                val displayName = profile?.username?.ifBlank { null } ?: userId
                val room = sessionRepo.createRoom(userId, displayName, campaignName.trim())
                startSessionObservers(room.id, userId, isDm = true)
                viewModelScope.launch { runCatching { userRepo.incrementSessionsPlayed(userId) } }
            }.onFailure { e ->
                _sessionState.value = SessionState.Error(e.message ?: "Failed to create room")
            }
        }
    }

    fun joinRoom(code: String, characterId: String) {
        val userId = authRepo.currentUserId() ?: run {
            _sessionState.value = SessionState.Error("Not signed in")
            return
        }
        viewModelScope.launch {
            _sessionState.value = SessionState.Loading
            runCatching {
                val profile = userRepo.getUserProfile(userId)
                val displayName = profile?.username?.ifBlank { null } ?: userId
                val character = characterRepo.getCharacter(userId, characterId)
                    ?: throw Exception("Character not found")
                val participant = SessionParticipant(
                    userId = userId,
                    displayName = displayName,
                    characterId = characterId,
                    characterName = character.name,
                    characterClass = character.characterClass,
                    characterLevel = character.level,
                    maxHp = character.maxHp,
                    currentHp = character.currentHp,
                    temporaryHp = character.temporaryHp,
                    armorClass = character.armorClass,
                    initiative = CharacterCalculator.modifier(character.dexterity),
                    conditions = character.conditions,
                    isConnected = true,
                    isDm = false,
                    avatarId = character.avatarId,
                    portraitPrimaryColor = character.portraitPrimaryColor,
                    portraitSecondaryColor = character.portraitSecondaryColor,
                )
                val roomId = sessionRepo.joinRoom(code.uppercase().trim(), participant)
                startSessionObservers(roomId, userId, isDm = false)
                viewModelScope.launch { runCatching { userRepo.incrementSessionsPlayed(userId) } }
            }.onFailure { e ->
                _sessionState.value = SessionState.Error(e.message ?: "Failed to join room")
            }
        }
    }

    private fun startSessionObservers(roomId: String, userId: String, isDm: Boolean = false) {
        activeRoomId = roomId
        activeUserId = userId
        _currentTurnIndex.value = 0
        sessionJob?.cancel()
        sessionJob = viewModelScope.launch {
            val entitiesFlow: Flow<List<DmEntity>> = if (isDm) {
                sessionRepo.observeDmEntities(roomId).catch { emit(emptyList()) }
            } else {
                flowOf(emptyList())
            }

            combine(
                sessionRepo.observeRoom(roomId),
                sessionRepo.observeParticipants(roomId),
                sessionRepo.observeRollFeed(roomId),
                entitiesFlow,
            ) { room, participants, rollFeed, dmEntities ->
                when {
                    // room==null is a transient cache miss, not a session end — stay Loading
                    room == null -> SessionState.Loading
                    // isActive==false means the DM explicitly ended the session
                    !room.isActive -> SessionState.Idle
                    else -> {
                        val me = participants.find { it.userId == userId }
                        val isDmUser = room.dmUserId == userId || me?.isDm == true
                        val unified = buildUnifiedInitiativeOrder(participants, dmEntities)
                        SessionState.InSession(
                            room = room,
                            participants = participants,
                            rollFeed = rollFeed,
                            myParticipant = me,
                            isDm = isDmUser,
                            initiativeOrder = unified,
                            dmEntities = dmEntities,
                        )
                    }
                }
            }
            .catch { e -> emit(SessionState.Error(e.message ?: "Session error")) }
            .collect { state ->
                _sessionState.value = state
            }
        }
    }

    private fun buildUnifiedInitiativeOrder(
        participants: List<SessionParticipant>,
        dmEntities: List<DmEntity>,
    ): List<InitiativeEntry> {
        val playerEntries = participants.map { InitiativeEntry.PlayerEntry(it) }
        val entityEntries = dmEntities
            .filter { !it.isDefeated }
            .map { InitiativeEntry.EntityEntry(it) }
        return (playerEntries + entityEntries).sortedByDescending { entry ->
            when (entry) {
                is InitiativeEntry.PlayerEntry -> entry.participant.initiativeRoll
                is InitiativeEntry.EntityEntry -> entry.entity.initiativeRoll
            }
        }
    }

    fun leaveRoom() {
        val roomId = activeRoomId ?: return
        val userId = activeUserId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.leaveRoom(roomId, userId) }
        }
        sessionJob?.cancel()
        sessionJob = null
        activeRoomId = null
        activeUserId = null
        _sessionState.value = SessionState.Idle
    }

    fun endSession() {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.endSession(roomId) }
        }
        sessionJob?.cancel()
        sessionJob = null
        activeRoomId = null
        activeUserId = null
        _sessionState.value = SessionState.Idle
    }

    fun updateMyHp(current: Int, temp: Int) {
        val roomId = activeRoomId ?: return
        val userId = activeUserId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.updateHp(roomId, userId, current.coerceAtLeast(0), temp.coerceAtLeast(0)) }
        }
    }

    fun updateMyConditions(conditions: List<String>) {
        val roomId = activeRoomId ?: return
        val userId = activeUserId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.updateConditions(roomId, userId, conditions) }
        }
    }

    fun rollInitiative() {
        val roomId = activeRoomId ?: return
        val userId = activeUserId ?: return
        val state = _sessionState.value as? SessionState.InSession ?: return
        val me = state.myParticipant ?: return

        val d20 = Random.nextInt(1, 21)
        val total = d20 + me.initiative
        viewModelScope.launch {
            runCatching {
                sessionRepo.updateInitiative(roomId, userId, total, me.initiative)
                val rollEvent = RollEvent(
                    userId = userId,
                    displayName = me.displayName,
                    characterName = me.characterName,
                    label = "Initiative",
                    expression = "1d20${if (me.initiative >= 0) "+${me.initiative}" else "${me.initiative}"}",
                    total = total,
                    isCrit = d20 == 20,
                    isCritFail = d20 == 1,
                    timestamp = currentTimeMillis(),
                )
                sessionRepo.postRoll(roomId, rollEvent)
            }
        }
    }

    fun postRollToFeed(roll: DiceRoll, label: String) {
        val roomId = activeRoomId ?: return
        val state = _sessionState.value as? SessionState.InSession ?: return
        val me = state.myParticipant
        val userId = activeUserId ?: return

        val expr = buildString {
            val groups = roll.dice
                .filter { !it.isDropped }
                .groupBy { it.diceType }
            groups.entries.forEachIndexed { i, (type, rolls) ->
                if (i > 0) append("+")
                append("${rolls.size}${type.label}")
            }
            if (roll.modifier != 0) {
                if (roll.modifier > 0) append("+${roll.modifier}") else append("${roll.modifier}")
            }
        }

        val rollEvent = RollEvent(
            userId = userId,
            displayName = me?.displayName ?: "",
            characterName = me?.characterName ?: "",
            label = label.ifBlank { roll.label },
            expression = expr,
            total = roll.total,
            isCrit = roll.isCrit,
            isCritFail = roll.isCritFail,
            timestamp = currentTimeMillis(),
        )
        viewModelScope.launch {
            runCatching { sessionRepo.postRoll(roomId, rollEvent) }
        }
    }

    fun advanceTurn() {
        val state = _sessionState.value as? SessionState.InSession ?: return
        val count = state.initiativeOrder.size
        if (count > 0) {
            _currentTurnIndex.value = (_currentTurnIndex.value + 1) % count
        }
    }

    fun requestAllInitiativeRolls() {
        val roomId = activeRoomId ?: return
        val userId = activeUserId ?: return
        val state = _sessionState.value as? SessionState.InSession ?: return
        _currentTurnIndex.value = 0
        viewModelScope.launch {
            runCatching {
                // Announce to players
                sessionRepo.postRoll(
                    roomId,
                    RollEvent(
                        userId = userId,
                        displayName = state.room.dmDisplayName,
                        characterName = "Dungeon Master",
                        label = "Everyone roll for Initiative!",
                        expression = "",
                        total = 0,
                        timestamp = currentTimeMillis(),
                    ),
                )
                // Auto-roll for every active entity
                state.dmEntities.filter { !it.isDefeated }.forEach { entity ->
                    val d20 = Random.nextInt(1, 21)
                    val dexMod = CharacterCalculator.modifier(entity.dexterity)
                    sessionRepo.updateDmEntityInitiative(roomId, entity.id, d20 + dexMod, dexMod)
                }
            }
        }
    }

    fun setParticipantInitiative(userId: String, value: Int) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.setParticipantInitiative(roomId, userId, value) }
        }
    }

    fun kickParticipant(userId: String) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.removeParticipant(roomId, userId) }
        }
    }

    fun updateParticipantHp(userId: String, current: Int, temp: Int) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching {
                sessionRepo.updateHp(roomId, userId, current.coerceAtLeast(0), temp.coerceAtLeast(0))
            }
        }
    }

    fun updateParticipantConditions(userId: String, conditions: List<String>) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.updateConditions(roomId, userId, conditions) }
        }
    }

    // ── DM Entity actions ─────────────────────────────────────────────────────

    fun addEntityFromMonster(monster: Monsters, instanceCount: Int) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching {
                repeat(instanceCount) { i ->
                    val instanceNumber = if (instanceCount > 1) i + 1 else null
                    val name = if (instanceNumber != null) "${monster.name} #$instanceNumber" else monster.name
                    val hp = monster.hit_points?.toInt() ?: 10
                    val entity = DmEntity(
                        id = generateEntityId(),
                        roomId = roomId,
                        name = name,
                        sourceType = DmEntitySource.COMPENDIUM,
                        monsterSlug = monster.slug,
                        maxHp = hp,
                        currentHp = hp,
                        armorClass = monster.armor_class?.toInt() ?: 10,
                        type = monster.type ?: "",
                        challengeRating = monster.challenge_rating ?: "",
                        createdAt = currentTimeMillis(),
                    )
                    sessionRepo.addDmEntity(entity)
                }
            }.onFailure { e ->
                println("[DmEntity] addEntityFromMonster failed: ${e::class.simpleName}: ${e.message}")
                _entityError.tryEmit(e.message ?: "Failed to add ${monster.name}")
            }
        }
    }

    fun addHomebrewEntity(entity: DmEntity) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching {
                val dexMod = CharacterCalculator.modifier(entity.dexterity)
                sessionRepo.addDmEntity(
                    entity.copy(
                        id = generateEntityId(),
                        roomId = roomId,
                        initiative = dexMod,
                        createdAt = currentTimeMillis(),
                    )
                )
            }.onFailure { e ->
                println("[DmEntity] addHomebrewEntity failed: ${e::class.simpleName}: ${e.message}")
                _entityError.tryEmit(e.message ?: "Failed to add entity")
            }
        }
    }

    fun updateEntityHp(entityId: String, current: Int, temp: Int) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching {
                sessionRepo.updateDmEntityHp(roomId, entityId, current.coerceAtLeast(0), temp.coerceAtLeast(0))
            }
        }
    }

    fun updateEntityConditions(entityId: String, conditions: List<String>) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.updateDmEntityConditions(roomId, entityId, conditions) }
        }
    }

    fun rollEntityInitiative(entityId: String) {
        val roomId = activeRoomId ?: return
        val state = _sessionState.value as? SessionState.InSession ?: return
        val entity = state.dmEntities.find { it.id == entityId } ?: return
        val d20 = Random.nextInt(1, 21)
        val dexMod = CharacterCalculator.modifier(entity.dexterity)
        val total = d20 + dexMod
        viewModelScope.launch {
            runCatching { sessionRepo.updateDmEntityInitiative(roomId, entityId, total, dexMod) }
        }
    }

    fun setEntityInitiative(entityId: String, roll: Int) {
        val roomId = activeRoomId ?: return
        val state = _sessionState.value as? SessionState.InSession ?: return
        val entity = state.dmEntities.find { it.id == entityId } ?: return
        val dexMod = CharacterCalculator.modifier(entity.dexterity)
        viewModelScope.launch {
            runCatching { sessionRepo.updateDmEntityInitiative(roomId, entityId, roll, dexMod) }
        }
    }

    fun toggleEntityDefeated(entityId: String) {
        val roomId = activeRoomId ?: return
        val state = _sessionState.value as? SessionState.InSession ?: return
        val entity = state.dmEntities.find { it.id == entityId } ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.toggleDmEntityDefeated(roomId, entityId, !entity.isDefeated) }
        }
    }

    fun removeEntity(entityId: String) {
        val roomId = activeRoomId ?: return
        viewModelScope.launch {
            runCatching { sessionRepo.removeDmEntity(roomId, entityId) }
        }
    }

    fun clearError() {
        if (_sessionState.value is SessionState.Error) {
            _sessionState.value = SessionState.Idle
        }
    }

    override fun onCleared() {
        super.onCleared()
        sessionJob?.cancel()
    }

    private fun generateEntityId(): String = buildString {
        repeat(32) { append("0123456789abcdef"[Random.nextInt(16)]) }
    }
}
